import { useState } from 'react';

export function DeveloperPlayground() {
  const [activeCodeTab, setActiveCodeTab] = useState<'react' | 'node' | 'python' | 'curl'>('react');
  const [copied, setCopied] = useState(false);

  const codeSnippets = {
    react: `import { ConsentProvider, ConsentWidget } from '@dpdp/sdk';

const client = createConsentClient({
  apiKey: process.env.DPDP_API_KEY,
});

export default function App() {
  return (
    <ConsentProvider client={client}>
      <ConsentWidget noticeId="privacy-v4.2" />
    </ConsentProvider>
  );
}`,
    node: `const { ConsentClient } = require('@dpdp/sdk/node');

const client = new ConsentClient({
  apiKey: process.env.DPDP_API_KEY,
});

// One call. All compliance primitives captured.
const record = await client.consents.record({
  userId:    'dp_usr_48213',
  noticeId: 'privacy-v4.2',
  granted:  true,
});
// record.notice_hash  === 'sha:a3f9c1b2'
// record.recorded_at  === '2026-07-18T09:41:22.041Z'`,
    python: `from dpdp import ConsentClient
import os

client = ConsentClient(api_key=os.getenv("DPDP_API_KEY"))

# Record user consent with full proof chain
record = client.consents.record(
    user_id="dp_usr_48213",
    notice_id="privacy-v4.2",
    granted=True,
    metadata={"channel": "web_checkout"}
)

print(f"Consent recorded: {record.consent_id}, hash: {record.notice_hash}")`,
    curl: `curl -X POST https://api.consentry.io/v1/consent/record \\
  -H "Authorization: Bearer ck_live_••••8f2a" \\
  -H "Content-Type: application/json" \\
  -d '{
    "userId":   "dp_usr_48213",
    "noticeId": "privacy-v4.2",
    "granted":  true
  }'`,
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(codeSnippets[activeCodeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="developers" className="relative py-28 px-6 lg:px-8 bg-sg border-y border-line">
      {/* Visual connector line */}
      <div className="sc" aria-hidden="true">
        <svg width="2" height="24" viewBox="0 0 2 24">
          <line x1="1" y1="0" x2="1" y2="24" className="sc-line" stroke="#2563EB" strokeOpacity="0.3" strokeWidth="1.5" />
        </svg>
        <span className="sc-node" />
      </div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <h2 className="text-[32px] sm:text-[40px] font-extrabold tracking-tight text-ink leading-[1.1]">
            One SDK call. Every compliance primitive, captured.
          </h2>
          <p className="mt-5 text-[16px] text-sub leading-relaxed max-w-[460px]">
            The record includes the notice version hash, the user session context, the application ID, and a precise UTC timestamp. No additional logging required on your end.
          </p>
          <div className="mt-8">
            <a href="#demo" className="btn-d inline-flex items-center gap-2 text-[13.5px]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              API Reference
              <span className="mono text-[10.5px] text-[#6B7280] border border-[#333] px-1.5 py-0.5 rounded ml-1">
                POST /v1/consent/record
              </span>
            </a>
          </div>
        </div>

        <div>
          <div className="rounded-xl3 bg-[#0B0F17] shadow-cardhover overflow-hidden">
            {/* Code Header Tabs */}
            <div className="flex items-center justify-between px-5 pt-3.5 border-b border-white/10 pb-0">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setActiveCodeTab('react')}
                  className={`code-tab text-[12.5px] pb-3.5 pt-0.5 px-3 border-b-2 font-medium cursor-pointer ${
                    activeCodeTab === 'react' ? 'active' : ''
                  }`}
                >
                  React
                </button>
                <button
                  type="button"
                  onClick={() => setActiveCodeTab('node')}
                  className={`code-tab text-[12.5px] pb-3.5 pt-0.5 px-3 border-b-2 font-medium cursor-pointer ${
                    activeCodeTab === 'node' ? 'active' : ''
                  }`}
                >
                  Node.js
                </button>
                <button
                  type="button"
                  onClick={() => setActiveCodeTab('python')}
                  className={`code-tab text-[12.5px] pb-3.5 pt-0.5 px-3 border-b-2 font-medium cursor-pointer ${
                    activeCodeTab === 'python' ? 'active' : ''
                  }`}
                >
                  Python
                </button>
                <button
                  type="button"
                  onClick={() => setActiveCodeTab('curl')}
                  className={`code-tab text-[12.5px] pb-3.5 pt-0.5 px-3 border-b-2 font-medium cursor-pointer ${
                    activeCodeTab === 'curl' ? 'active' : ''
                  }`}
                >
                  cURL
                </button>
              </div>

              <button
                type="button"
                onClick={handleCopyCode}
                className="text-[11.5px] text-[#6B7280] hover:text-[#9CA3AF] flex items-center gap-1.5 pb-3.5 transition cursor-pointer"
              >
                {copied ? (
                  <>
                    <svg className="w-3.5 h-3.5 text-emerald" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-emerald">Copied</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>

            {/* Install Banner */}
            <div className="px-5 py-3 border-b border-white/5">
              <span className="mono text-[12px] text-emerald">$ npm install @dpdp/sdk</span>
            </div>

            {/* Pre/Code Block */}
            <pre className="px-5 pt-4 pb-4 overflow-x-auto text-[12.5px] leading-[1.75] text-[#E5E7EB] mono">
              <code>{codeSnippets[activeCodeTab]}</code>
            </pre>

            {/* Response Preview */}
            <div className="mx-5 mb-5 border border-white/10 rounded-xl p-4 bg-white/5">
              <div className="text-[10.5px] text-[#4B5563] mono mb-2">Response · 200 OK · 38ms</div>
              <pre className="mono text-[11px] text-[#9CA3AF] leading-relaxed">
{`{
  "consent_id": "cns_01jf8a...",
  "notice_hash": "sha:a3f9c1b2",
  "recorded_at": "2026-07-18T09:41:22.041Z",
  "certificate_id": "CERT-2026-0091"
}`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
